import React from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { fmtMXN, STATUS_LABEL, STATUS_COLOR } from '@/lib/format';
import PeriodPicker from '@/components/PeriodPicker';
import { usePeriodSummary } from '@/hooks/usePeriodSummary';
import { AlertTriangle, TrendingUp, Coins, Receipt, Landmark, Wallet, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const StatCard = ({ label, value, sub, icon: Icon, tint = 'primary', tid }) => (
  <Card data-testid={tid} className="p-4 border-border rounded-xl">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-bold">{label}</p>
        <p className="font-display text-2xl font-extrabold tracking-tight mt-2 num">{value}</p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </div>
      <div className={`h-9 w-9 rounded-lg flex items-center justify-center bg-${tint}/10 text-${tint}`}>
        <Icon className="h-4 w-4"/>
      </div>
    </div>
  </Card>
);

export default function Dashboard() {
  const { data, loading } = usePeriodSummary();

  if (loading) {
    return <div className="pt-8 text-center text-muted-foreground">Cargando…</div>;
  }
  if (!data) return null;

  const { period, summary, alerts, activities, counts } = data;

  return (
    <div className="space-y-5 pt-1">
      <PeriodPicker/>

      <div className="flex items-center gap-2">
        <Badge data-testid="period-status" className={`${STATUS_COLOR[period.status]} border-0 font-semibold text-[11px] tracking-wide rounded-full px-3 py-1`}>
          {STATUS_LABEL[period.status]}
        </Badge>
        {period.locked && <Badge className="bg-secondary text-foreground border border-border rounded-full">Bloqueado</Badge>}
      </div>

      <Card className="p-5 border-border rounded-2xl bg-primary/5 border-primary/20">
        <p className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-bold">Ingresos totales del mes</p>
        <p className="font-display text-4xl font-extrabold tracking-tighter mt-2 num" data-testid="stat-ingresos-totales">{fmtMXN(summary.ingresos_totales)}</p>
        <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
          {activities.map(a => (
            <div key={a.id} className="rounded-lg bg-background/60 border border-border p-2.5">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{a.name}</p>
              <p className="font-semibold num mt-0.5">{fmtMXN(summary.ingresos_por_actividad?.[a.id] || 0)}</p>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-3">
        <StatCard tid="stat-isr" label="ISR RESICO est." value={fmtMXN(summary.isr_estimado)} sub={`Tasa ${(summary.isr_tasa * 100).toFixed(2)}%`} icon={TrendingUp}/>
        <StatCard tid="stat-iva-pagar" label="IVA a pagar" value={fmtMXN(summary.iva_a_pagar)} sub={summary.iva_a_favor ? `A favor: ${fmtMXN(summary.iva_a_favor)}` : ''} icon={Coins}/>
        <StatCard tid="stat-gastos" label="Gastos" value={fmtMXN(summary.total_gastos)} sub={`${counts.expenses} registro(s)`} icon={Receipt}/>
        <StatCard tid="stat-depositos" label="Depósitos" value={fmtMXN(summary.total_depositos)} sub={`Efectivo pend.: ${fmtMXN(summary.efectivo_pendiente)}`} icon={Landmark}/>
      </div>

      {alerts?.length > 0 && (
        <Card className="p-4 border-border rounded-xl" data-testid="alerts-card">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-amber-500"/>
            <p className="text-[11px] uppercase tracking-widest font-bold">Alertas</p>
          </div>
          <ul className="space-y-2">
            {alerts.map((a, i) => (
              <li key={i} data-testid={`alert-${a.code}`} className="text-sm flex items-start gap-2 pt-1">
                <span className={`mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0 ${a.level === 'danger' ? 'bg-destructive' : a.level === 'warning' ? 'bg-amber-500' : 'bg-primary'}`}/>
                <span>{a.message}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Link to="/ventas"><Button data-testid="quick-add-sale" variant="outline" className="w-full h-12 rounded-lg justify-between"><span className="font-medium">Ventas <span className="text-muted-foreground">({counts.sales})</span></span><ArrowUpRight className="h-4 w-4"/></Button></Link>
        <Link to="/gastos"><Button data-testid="quick-add-expense" variant="outline" className="w-full h-12 rounded-lg justify-between"><span className="font-medium">Gastos <span className="text-muted-foreground">({counts.expenses})</span></span><ArrowUpRight className="h-4 w-4"/></Button></Link>
        <Link to="/depositos"><Button data-testid="quick-add-deposit" variant="outline" className="w-full h-12 rounded-lg justify-between"><span className="font-medium">Depósitos <span className="text-muted-foreground">({counts.deposits})</span></span><ArrowUpRight className="h-4 w-4"/></Button></Link>
        <Link to="/preformulario"><Button data-testid="quick-preform" variant="outline" className="w-full h-12 rounded-lg justify-between"><span className="font-medium">Preformulario</span><ArrowUpRight className="h-4 w-4"/></Button></Link>
      </div>

      <p className="text-[11px] text-muted-foreground leading-relaxed pt-2">
        Los cálculos son estimaciones pendientes de revisión. La app no presenta declaraciones ni inicia sesión automáticamente en el portal del SAT.
      </p>
    </div>
  );
}
